import { useBalance } from '@/components/future/data';
import { buysell } from '@/components/future/func';
import { OrderType, Type } from '@/components/future/type';
import SpinLoading from '@/components/loading/SpinLoading';
import { toastApp } from '@/components/toast-message/sonner';
import { HistoryTradeItem } from '@/components/trading-panel/hooks/useGetHistory';
import { percentage } from '@/components/trading-panel/hooks/useGetPosition';
import { useCheckClosePair } from '@/hooks/useCheckClosePair';
import { usePairs } from '@/store/pairs';
import { useAddress } from '@/store/selector';
import { formatDisplay } from '@/utils/format-display';
import { numberFormat } from '@/utils/numberFormat';
import BigNumber from 'bignumber.js';
import clsx from 'clsx';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import SheetDetail from './sheet-detail';

type Props = {
    item: HistoryTradeItem;
};

const MAP_TYPE = {
    0: {
        title: 'Take profit',
        color: '#38F24A',
        bg: 'rgba(56, 242, 74, 0.10)',
    },
    1: {
        title: 'Stop Loss',
        color: '#FF695F',
        bg: 'rgba(255, 105, 95, 0.10)',
    },

    2: {
        title: 'Liquidated',
        color: '#FF695F',
        bg: 'rgba(255, 105, 95, 0.10)',
    },
} as Record<number, { title: string; color: string; bg: string }>;

export default function Item({ item }: Props) {
    const { t } = useTranslation();

    const [isOpenDetail, setIsOpenDetail] = useState(false);

    const [isLoading, setIsLoading] = useState(false);

    const { isClose } = useCheckClosePair(Number(item.pairIndex));

    const navigate = useNavigate();

    const address = useAddress();

    const { data: usdcBalance } = useBalance();

    const pairs = usePairs((state) => state.data);

    const pairConfig = useMemo(
        () => pairs.find((p) => p.pairIndex === Number(item.pairIndex)),
        [pairs, item.pairIndex]
    );

    const handleOpen = useCallback(() => {
        setIsOpenDetail(true);
    }, []);

    const handleCloseDetail = useCallback(() => {
        setIsOpenDetail(false);
    }, []);

    const handleReopenOrder = async (isBuy: boolean) => {
        if (
            new BigNumber(usdcBalance?.toString() ?? '0').lt(
                item.collateralBeforeFee
            )
        ) {
            return toastApp.error(t('Not enough balance'));
        }

        if (
            pairConfig?.maxPosition &&
            Number(item.leverage) *
                new BigNumber(item.collateralBeforeFee)
                    .dividedBy(1e18)
                    .toNumber() >
                pairConfig.maxPosition
        ) {
            return toastApp.error(
                `Max position size is ${pairConfig.maxPosition}`
            );
        }

        setIsLoading(true);
        await buysell({
            address,
            buy: isBuy,
            onSuccess: () => {
                navigate('/orders?tab=Position', {
                    state: { isOpened: true },
                });
            },
        })({
            pairIndex: item.pairIndex.toString(),
            leverage: item.leverage,
            amount: new BigNumber(item.collateralBeforeFee)
                .dividedBy(1e18)
                .toString(),
            orderType: OrderType.Market,
            tp: '',
            sl: '',
            type: isBuy ? Type.Long : Type.Short,
            limitPrice: '',
        });

        setIsLoading(false);
    };

    const orderType = useMemo(() => {
        if (item.orderType === null) {
            return {
                title: 'Closed',
                color: '#fff',
                bg: 'rgba(255, 255, 255, 0.10)',
            };
        }
        return MAP_TYPE[item.orderType];
    }, [item.orderType]);

    return (
        <>
            <div
                className="bg-[#141414] rounded-2xl p-5 mb-3"
                onClick={handleOpen}
            >
                <div className="flex flex-row  gap-1 items-center justify-between">
                    <div className="flex items-center gap-x-2">
                        <img
                            src={item.logoUrl}
                            alt={item.symbol}
                            className="w-6 h-6 rounded-full"
                        />
                        <span className="text-xl text-white">
                            {/* {item.pairName.split('USD')[0]} */}
                            {item.nameDisplay}
                        </span>

                        {!isClose && (
                            <button
                                data-action="ReopenOrder"
                                disabled={isLoading}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleReopenOrder(item.buy);
                                }}
                                className={clsx(
                                    'text-green1 px-2 rounded-3xl  h-6 flex flex-col text-sm font-medium justify-center items-center',
                                    {
                                        'opacity-50': isLoading,
                                    }
                                )}
                            >
                                {isLoading ? (
                                    <SpinLoading className="!w-4 !h-4" />
                                ) : (
                                    t('ReOpen')
                                )}
                            </button>
                        )}
                    </div>

                    <div className="flex items-center gap-x-2">
                        <p
                            className={clsx(
                                'uppercase text-[11px] leading-4 border rounded-md ml-2 px-1',
                                {
                                    'text-green1 border-green1': !!item.buy,
                                    '!text-red1 !border-red1': !item.buy,
                                }
                            )}
                        >
                            {item.buy ? 'Long' : 'Short'}
                        </p>

                        <p className="uppercase text-[11px] leading-4 text-white border-white border rounded-md px-1.5">
                            {item.leverage}x
                        </p>
                    </div>
                </div>

                <div
                    className={clsx(
                        'rounded-2xl px-2 py-3 flex flex-col justify-center items-center mt-3 relative unrealized-box',
                        {
                            'bg-transparent2': item.pnlNumber >= 0,
                            'bg-transparentRed2': item.pnlNumber < 0,
                        }
                    )}
                >
                    <p
                        className={clsx(
                            'text-left text-sm text-[#666] opacity-40 mb-1',
                            {
                                'text-green1': item.pnlNumber >= 0,
                                'text-[#FF695F]': item.pnlNumber < 0,
                            }
                        )}
                    >
                        {'PNL'}: {percentage(item.pnlPercent)}
                    </p>

                    <p
                        className={clsx(
                            'uppercase text-lg leading-4 text-left flex items-center mt-1 justify-start ',
                            {
                                'text-green1': item.pnlNumber >= 0,
                                'text-[#FF695F]': item.pnlNumber < 0,
                            }
                        )}
                    >
                        {item.pnlNumber > 0 ? '+' : ''}
                        {formatDisplay(item.pnlNumber, {
                            decimalToShow: 2,
                        })}{' '}
                        WLD
                    </p>

                    {/* Svg Animate */}
                    <svg
                        height="100%"
                        width="100%"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <rect
                            stroke={
                                item.pnlNumber >= 0
                                    ? 'rgba(56, 242, 74, 0.20)'
                                    : 'rgba(255, 105, 95, 0.20)'
                            }
                            rx="8"
                            ry="8"
                            className="line"
                            height="100%"
                            width="100%"
                            strokeLinejoin="round"
                        />
                    </svg>
                </div>

                <div className="flex flex-row self-stretch gap-2 items-center justify-between mt-3">
                    <div className="flex flex-col gap-1">
                        <p className="text-start text-xs text-[#666]">
                            {t('Amount')}
                        </p>
                        <p className="uppercase text-sm text-white text-start">
                            {numberFormat(
                                Number(item.initialPosToken) /
                                    Math.pow(10, item.decimals)
                            )}{' '}
                            WLD
                        </p>
                    </div>

                    <div className="flex flex-col gap-1">
                        <p className="text-start  text-xs text-[#666] leading-4">
                            {t('Entry Price')}
                        </p>
                        <p className="uppercase text-sm text-white text-start">
                            {formatDisplay(
                                Number(item.openPrice) / Math.pow(10, 10),
                                {
                                    decimalToShow: item.decimalToShow,
                                }
                            )}
                        </p>
                    </div>

                    <div className="flex flex-col gap-1">
                        <p className="text-start text-xs text-[#666]">
                            {t('Close Price')}
                        </p>
                        <p className="uppercase text-sm text-white text-start">
                            {formatDisplay(
                                Number(item.closePrice) / Math.pow(10, 10),
                                {
                                    decimalToShow: item.decimalToShow,
                                }
                            )}
                        </p>
                    </div>
                </div>

                <div className="flex justify-between items-center mt-4.5 gap-x-4">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="100%"
                        height="6"
                        viewBox="0 0 97 6"
                        fill="none"
                        preserveAspectRatio="none"
                    >
                        <path
                            d="M96.6667 3C96.6667 4.47276 95.4728 5.66667 94 5.66667C92.5272 5.66667 91.3333 4.47276 91.3333 3C91.3333 1.52724 92.5272 0.333333 94 0.333333C95.4728 0.333333 96.6667 1.52724 96.6667 3ZM94 3V3.5H92.0417V3V2.5H94V3ZM88.125 3V3.5H84.2083V3V2.5H88.125V3ZM80.2917 3V3.5H76.375V3V2.5H80.2917V3ZM72.4583 3V3.5H68.5417V3V2.5H72.4583V3ZM64.625 3V3.5H60.7083V3V2.5H64.625V3ZM56.7917 3V3.5H52.875V3V2.5H56.7917V3ZM48.9583 3V3.5H45.0417V3V2.5H48.9583V3ZM41.125 3V3.5H37.2083V3V2.5H41.125V3ZM33.2917 3V3.5H29.375V3V2.5H33.2917V3ZM25.4583 3V3.5H21.5417V3V2.5H25.4583V3ZM17.625 3V3.5H13.7083V3V2.5H17.625V3ZM9.79167 3V3.5H5.875V3V2.5H9.79167V3ZM1.95834 3V3.5H0V3V2.5H1.95834V3Z"
                            fill="#292929"
                        />
                    </svg>

                    <div
                        className={clsx(
                            'text-sm px-2 rounded-3xl py-[2px] shrink-0',
                            {
                                'border border-[#FF695F]': item.orderType === 2,
                            }
                        )}
                        style={{
                            background: orderType.bg,
                            color: orderType.color,
                        }}
                    >
                        {t(orderType.title)}
                    </div>

                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="100%"
                        height="6"
                        viewBox="0 0 97 6"
                        fill="none"
                        preserveAspectRatio="none"
                    >
                        <path
                            d="M0.333333 3C0.333333 4.47276 1.52724 5.66667 3 5.66667C4.47276 5.66667 5.66667 4.47276 5.66667 3C5.66667 1.52724 4.47276 0.333333 3 0.333333C1.52724 0.333333 0.333333 1.52724 0.333333 3ZM3 3V3.5H4.95833V3V2.5H3V3ZM8.875 3V3.5H12.7917V3V2.5H8.875V3ZM16.7083 3V3.5H20.625V3V2.5H16.7083V3ZM24.5417 3V3.5H28.4583V3V2.5H24.5417V3ZM32.375 3V3.5H36.2917V3V2.5H32.375V3ZM40.2083 3V3.5H44.125V3V2.5H40.2083V3ZM48.0417 3V3.5H51.9583V3V2.5H48.0417V3ZM55.875 3V3.5H59.7917V3V2.5H55.875V3ZM63.7083 3V3.5H67.625V3V2.5H63.7083V3ZM71.5417 3V3.5H75.4583V3V2.5H71.5417V3ZM79.375 3V3.5H83.2917V3V2.5H79.375V3ZM87.2083 3V3.5H91.125V3V2.5H87.2083V3ZM95.0417 3V3.5H97V3V2.5H95.0417V3Z"
                            fill="#292929"
                        />
                    </svg>
                </div>
            </div>

            {isOpenDetail && (
                <SheetDetail
                    item={item}
                    isClose={isClose}
                    isOpen={isOpenDetail}
                    onClose={handleCloseDetail}
                />
            )}
        </>
    );
}
